// Per stelling van een matrixvraag: hoe vaak is elk antwoord gekozen.
//
// Nodig omdat useMatrixData per stelling alleen een gemiddelde berekent (of,
// bij tekstantwoorden, een losse telling zonder volgorde en zonder de antwoorden
// die niemand gaf). Drenthe beweegt wilde per stelling zien hoeveel mensen
// 'Helemaal mee eens', 'Mee eens' enzovoort kozen (updateverzoek 2026-10-02).
//
// Een antwoord hangt aan zijn stelling via RowID en aan zijn kolom via
// ColumnID; de teksten zijn alleen de terugval voor oude documenten.
// AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows

import { buildRowLookup, rowKeyFor, resolveRow } from '../analysisHelpers/matrixRowLookup'
import matrixColumnLabel from '../questionnaire/matrixColumnLabel'
import { sortMatrixColumns, isScaleColumn } from '../questionnaire/matrixScaleColumns'
import formatMomentLabel from '../formatMomentLabel'
import { getDivergingColors, OTHER_BUCKET_COLOR } from '../chartPalette'

/**
 * @typedef {object} DistributionCategory
 * @property {string} key - ColumnID, of 'input:<tekst>' voor een antwoord zonder kolom
 * @property {string} label - Wat er op de as of in de legenda staat
 * @property {boolean} ordinal - Hoort bij de schaal (anders: 'Niet van toepassing' of onbekend)
 */

/**
 * @typedef {object} DistributionRow
 * @property {string} key - Stabiele sleutel van de stelling
 * @property {string} title - De stelling
 * @property {Object<string, number>} counts - Aantal per categoriesleutel, 0 als niemand hem koos
 * @property {number} total - Aantal antwoorden op deze stelling
 */

/**
 * @typedef {object} MomentDistribution
 * @property {string} moment - 'Voormeting (1)', of 'Meetmoment' als het meetmoment onbekend is
 * @property {number|null} momentPosition
 * @property {DistributionCategory[]} categories - In schaalvolgorde
 * @property {DistributionRow[]} rows - In stellingvolgorde
 */

/**
 * @param {*} raw - MomentPosition van een antwoord
 * @returns {number|null}
 */
const toPosition = (raw) => {
    const pos = typeof raw === 'number' ? raw : parseInt(raw, 10)
    return Number.isFinite(pos) ? pos : null
}

/**
 * @param {object} params
 * @param {Array<object>} params.records - Antwoorden uit useHierarchyData
 * @param {Array<{ID?: string, Title?: string, posNeg?: string}>} params.rowDefs - Stellingen, in volgorde
 * @param {Array<{ID: string, Position?: number, Title?: string|number}>} params.columns - Antwoordkolommen
 * @param {number} params.upperScale - ReachEnd van de vraag; kolommen daarboven horen niet bij de schaal
 * @param {Object<number, string>} [params.pos2title] - Position → titel van het meetmoment
 * @returns {MomentDistribution[]}
 */
const buildMatrixDistribution = ({ records, rowDefs, columns, upperScale, pos2title = {} }) => {
    // Zelfde regel als de schuifbalk (vasScale.js): één bron, matrixScaleColumns.js.
    const sortedColumns = sortMatrixColumns(columns)
    const columnCategories = sortedColumns.map((column, index) => ({
        key: column.ID,
        label: matrixColumnLabel(column, index),
        ordinal: isScaleColumn(column, index, upperScale),
    }))
    const byColumnId = new Map(columnCategories.map(c => [c.key, c]))
    const byLabel = new Map(columnCategories.map(c => [c.label, c]))

    // Stellingen in de volgorde van de vragenlijst; ze komen ongesorteerd uit
    // Firestore. ID als laatste sleutel, zodat een gelijke Position niet van de
    // invoervolgorde afhangt.
    // AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
    const sortedRows = [...(rowDefs || [])].sort((a, b) =>
        ((Number(a.Position) || 0) - (Number(b.Position) || 0)) || String(a.ID).localeCompare(String(b.ID)))
    const lookup = buildRowLookup(sortedRows)
    const answers = (records || []).filter(r => String(r?.Input ?? '').trim() !== '')
    if (answers.length === 0) return []

    /** Welke categorie hoort bij dit antwoord: kolom-ID, dan kolomtekst, dan het antwoord zelf. */
    const extraCategories = new Map()
    const categoryOf = (record) => {
        if (record.ColumnID && byColumnId.has(record.ColumnID)) return byColumnId.get(record.ColumnID)
        const text = String(record.Input).trim()
        if (byLabel.has(text)) return byLabel.get(text)
        const key = `input:${text}`
        if (!extraCategories.has(key)) extraCategories.set(key, { key, label: text, ordinal: false })
        return extraCategories.get(key)
    }
    answers.forEach(categoryOf)

    // Zonder kolommen (templategegevens) is er geen schaalvolgorde; getallen
    // dan op grootte, tekst in de volgorde waarin hij voorkwam.
    const extras = [...extraCategories.values()]
    if (columnCategories.length === 0 && extras.every(c => Number.isFinite(Number(c.label)))) {
        extras.sort((a, b) => Number(a.label) - Number(b.label))
    }
    const categories = [...columnCategories, ...extras]

    const positions = [...new Set(answers.map(r => toPosition(r.MomentPosition)))]
        .sort((a, b) => (a === null ? Infinity : a) - (b === null ? Infinity : b))

    return positions.map(pos => {
        const inMoment = answers.filter(r => toPosition(r.MomentPosition) === pos)
        const emptyCounts = () => Object.fromEntries(categories.map(c => [c.key, 0]))

        /** @type {Map<string, DistributionRow>} */
        const rows = new Map(sortedRows.map(def => {
            const key = rowKeyFor(lookup, { RowID: def.ID, RowTitle: def.Title })
            return [key, { key, title: def.Title || '', counts: emptyCounts(), total: 0 }]
        }))

        inMoment.forEach(record => {
            const key = rowKeyFor(lookup, record)
            if (!rows.has(key)) {
                const title = resolveRow(lookup, record)?.Title || record.RowTitle || ''
                rows.set(key, { key, title, counts: emptyCounts(), total: 0 })
            }
            const row = rows.get(key)
            row.counts[categoryOf(record).key] += 1
            row.total += 1
        })

        return {
            moment: formatMomentLabel(pos === null ? undefined : pos2title[pos], pos),
            momentPosition: pos,
            categories,
            rows: [...rows.values()],
        }
    })
}

/**
 * Gegevens voor de staafgrafiek: één groep per antwoord, een staaf per stelling.
 *
 * @param {MomentDistribution} moment
 * @returns {Array<Object<string, string|number>>} { name: antwoord, [stellingsleutel]: aantal }
 */
export const toDistributionBarData = (moment) => moment.categories.map(category => ({
    name: category.label,
    ...Object.fromEntries(moment.rows.map(row => [row.key, row.counts[category.key] || 0])),
}))

/**
 * Kleur per antwoord. Een schaal is ordinaal, dus het divergerende palet (rood
 * voor oneens, teal voor eens); wat buiten de schaal valt ('Niet van
 * toepassing', een onbekend antwoord) krijgt het rustige neutraal.
 * AI-CONTEXT: See docs/features/chart-color-system.md
 *
 * @param {DistributionCategory[]} categories
 * @returns {Object<string, string>} categoriesleutel → kleur
 */
export const categoryColors = (categories) => {
    const scale = categories.filter(c => c.ordinal)
    const colors = getDivergingColors(scale.length)
    return Object.fromEntries(categories.map(c => [
        c.key,
        c.ordinal ? colors[scale.indexOf(c)] : OTHER_BUCKET_COLOR,
    ]))
}

export default buildMatrixDistribution
